import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { resizeImage } from "../../utils/image";

const NewsFormPage = () => {
  const token = localStorage.getItem("admin_jwt");
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [image, setImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [originalImageUrl, setOriginalImageUrl] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // 詳細取得
  useEffect(() => {
    if (isEdit) {
      fetch(`/api/admin/news?id=${encodeURIComponent(id)}`, {
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      })
        .then((res) => res.json())
        .then((data) => {
          setTitle(data.title || "");
          setBody(data.body || "");
          setPreviewUrl(data.image || "");
          setOriginalImageUrl(data.image || "");
        });
    }
  }, [id, isEdit, token]);

  // 保存処理（追加・編集共通）
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return; // 二重送信ガード
    setIsSubmitting(true);

    try {
    let imageUrl = previewUrl;

    if (image) {
      try {
        const formData = new FormData();
        formData.append("file", image);

        const res = await fetch("/api/blob/upload", {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${token}`,
          },
          body: formData,
        });

        if (!res.ok) {
          setPreviewUrl(originalImageUrl);
          const text = await res.text();
          alert("画像アップロードに失敗しました: " + text);
          return;
        }

        const data = await res.json();
        imageUrl = data.url;
      } catch (err) {
        alert("画像アップロード中にエラーが発生しました");
        console.error(err);
        return;
      }
    }

    const payload = {
      id,      // 追加時は undefined
      title,
      body,
      imageUrl,
    };

    // /api/admin/news (POST) で保存（追加・編集兼用）
    const response = await fetch("/api/admin/news", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    // 編集時に画像が変更されていたら元画像を削除
    if (isEdit && image && originalImageUrl && originalImageUrl !== imageUrl) {
      try {
        const delRes = await fetch("/api/blob/delete", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
          body: JSON.stringify({ url: originalImageUrl }),
        });
        if (!delRes.ok) {
          console.warn("元画像削除に失敗:", await delRes.text());
        }
      } catch (err) {
        console.warn("画像削除APIエラー:", err);
      }
    }

    if (response.ok) {
      navigate("/admin/news");
    } else {
      alert("保存に失敗しました");
    }
    } finally {
      setIsSubmitting(false);
    }
  };

  // 削除
  const handleDelete = async () => {
    if (isDeleting) return; // 二重削除ガード
    if (!window.confirm("このお知らせを削除してもよろしいですか？")) return;
    setIsDeleting(true);

    try {
      // 統合API：deleteも/api/admin/news (DELETE)に集約（idをbodyに含める）
      const res = await fetch("/api/admin/news", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({ id }),
      });

      if (res.ok) {
        alert("削除しました");
        navigate("/admin/news");
      } else {
        alert("削除に失敗しました");
      }
    } catch (err) {
      console.error("削除中にエラー:", err);
      alert("削除中にエラーが発生しました");
    } finally {
      setIsDeleting(false);
    }
  };

  // 添付画像の取り消し（新規選択分をキャンセルし、編集時は元画像に戻す）
  const handleClearImage = () => {
    if (previewUrl && previewUrl.startsWith("blob:"))
      URL.revokeObjectURL(previewUrl);
    setImage(null);
    setPreviewUrl(originalImageUrl || "");
  };

  return (
    <form onSubmit={handleSubmit} className="adm-form adm-card max-w-xl mx-auto">
      <h2 className="adm-title mb-4">{isEdit ? "お知らせ編集" : "お知らせ追加"}</h2>
      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="タイトル"
        className="adm-input mb-4"
        required
      />
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="本文"
        className="adm-input mb-4"
        rows={6}
        required
      />
      <input
        type="file"
        name="file"
        accept="image/*"
        onChange={async (e) => {
          const f = e.target.files[0];
          e.target.value = ""; // 同じファイルを選び直せるように
          if (!f) return;
          const resized = await resizeImage(f);
          if (previewUrl && previewUrl.startsWith("blob:"))
            URL.revokeObjectURL(previewUrl);
          setImage(resized);
          setPreviewUrl(URL.createObjectURL(resized));
        }}
        className="mb-4"
      />
      {previewUrl && (
        <div className="mb-4">
          <img src={previewUrl} alt="preview" className="max-h-40 object-contain" />
          <button
            type="button"
            onClick={handleClearImage}
            className="mt-1 block text-sm text-red-600 hover:underline"
          >
            取り消し
          </button>
        </div>
      )}
      <div className="flex items-center gap-2">
        <button type="submit" disabled={isSubmitting} className="adm-btn">
          {isSubmitting ? "保存中…" : isEdit ? "保存" : "追加"}
        </button>
        <button
          type="button"
          onClick={() => navigate(isEdit ? `/admin/news/${id}` : "/admin/news")}
          className="adm-btn-sub"
        >
          キャンセル
        </button>
        {isEdit && (
          <button
            type="button"
            onClick={handleDelete}
            disabled={isDeleting}
            className="adm-btn-danger ml-auto"
          >
            {isDeleting ? "削除中…" : "削除"}
          </button>
        )}
      </div>
    </form>
  );
};

export default NewsFormPage;
